import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlayer } from '../contexts/PlayerContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, Save, Download, Search, Plus, Trash2, Music, 
  Play, Pause, Upload, CheckCircle2, Eye, EyeOff,
  Library, Image, BarChart3, FileDown, Check, AlertCircle,
  ChevronRight, Loader2, TrendingUp, Clock, Users, Sparkles
} from 'lucide-react';
import { API_URL } from '../utils/api';
import { dotForGroup } from '../utils/groupColors';
import { MUSICAL_KEYS } from '../utils/musicalKeys';
import { isPackTempo } from '../utils/packTempo';
import { loadCompletePackLoops } from '../utils/loadCompletePackLoops';

const PACK_GROUPS = ['Melodies', 'Drums', 'Textures', 'Sound FX'];
const GROUP_MAP = {
  'Melodies': 'melody_loop_ids',
  'Drums': 'drum_loop_ids',
  'Textures': 'texture_loop_ids',
  'Sound FX': 'sfx_loop_ids'
};

const MOOD_CATEGORIES = [
  "Calm", "Happy", "Sad", "Energetic", "Angry", "Confident", 
  "Reflective", "Hopeful", "Chill", "Dark", "Focused", "Warm", "Playful", "Tense"
];

const STYLE_TAGS = [
  "Trap", "R&B", "Afrobeats", "Latin", "Lo-Fi", "Boom Bap", "Drill", 
  "Pop", "Soul", "Jazz", "Electronic", "Acoustic", "Ambient", "Cinematic",
  "Hip Hop", "Rap", "Country", "Rock"
];

const USE_CASES = [
  "Class", "Therapy/SEL", "Event", "Seasonal", "Partner", "Curriculum", "Staff Picks", "Occasion"
];



// Progress Step Component
const ProgressStep = ({ step, currentStep, title, isComplete }) => {
  const isActive = step === currentStep;
  const isPast = step < currentStep || isComplete;
  
  return (
    <div className="flex items-center gap-2">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
        isPast ? 'bg-green-500 text-white' :
        isActive ? 'bg-primary text-black' :
        'bg-white/10 text-muted-foreground'
      }`}>
        {isPast ? <Check className="w-4 h-4" /> : step}
      </div>
      <span className={`text-sm ${isActive ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
        {title}
      </span>
    </div>
  );
};

const SoundPackBuilder = () => {
  const { pack_id } = useParams();
  const { isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const { playLoop, currentLoop, isPlaying, stop } = usePlayer();
  const navigate = useNavigate();
  const coverInputRef = useRef(null);
  
  // Pack state
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loopsReady, setLoopsReady] = useState(false);
  const [loadingLoops, setLoadingLoops] = useState(false);
  const [loopsLoadError, setLoopsLoadError] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('metadata');
  
  // Loops in pack (by group)
  const [packLoops, setPackLoops] = useState({
    Melodies: [],
    Drums: [],
    Textures: [],
    'Sound FX': []
  });
  
  // Edit form
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    tempo_set: '',
    key: '',
    moods: [],
    styles: [],
    use_case: '',
    status: 'draft',
    pack_code: ''  // Phase 1: Link to loops by pack_code
  });
  
  // Loop Picker state
  const [showLoopPicker, setShowLoopPicker] = useState(false);
  const [pickerGroup, setPickerGroup] = useState('Melodies');
  const [filterByGroup, setFilterByGroup] = useState(false); // Toggle to filter by instrument group
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMood, setSearchMood] = useState('');
  const [searchStyle, setSearchStyle] = useState('');
  const [searchKey, setSearchKey] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedLoops, setSelectedLoops] = useState([]);
  const [searchError, setSearchError] = useState(null);
  
  // Load from Pack state (Phase 1)
  const [showLoadFromPack, setShowLoadFromPack] = useState(false);
  const [packCodes, setPackCodes] = useState([]);
  const [loadingPackCodes, setLoadingPackCodes] = useState(false);
  const [selectedPackCode, setSelectedPackCode] = useState('');
  const [loadingFromPack, setLoadingFromPack] = useState(false);
  
  // Insights state
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  
  // Cover image
  const [uploadingCover, setUploadingCover] = useState(false);
  
  // Calculate current step for progress
  const hasMetadata = editForm.title && isPackTempo(editForm.tempo_set) && editForm.key;
  const totalLoops = Object.values(packLoops).reduce((sum, loops) => sum + loops.length, 0);
  const hasLoops = totalLoops > 0;
  const isPublished = editForm.status === 'published';
  
  const getCurrentStep = () => {
    if (!hasMetadata) return 1;
    if (!hasLoops) return 2;
    if (!isPublished) return 3;
    return 4;
  };
  
  // Check auth
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/admin/login');
    }
  }, [authLoading, isAuthenticated, isAdmin, navigate]);
  
  // Fetch pack
  const fetchPack = useCallback(async () => {
    setLoopsReady(false);
    try {
      const response = await fetch(`${API_URL}/api/admin/sound-packs/${pack_id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPack(data);
        
        // Set edit form with new fields
        setEditForm({
          title: data.title || data.name || '',
          description: data.description || '',
          tempo_set: String(data.tempo_set || data.bpm || ''),
          key: data.key || '',
          moods: data.moods || (data.mood_category ? [data.mood_category] : []),
          styles: data.styles || (data.style_genre ? [data.style_genre] : []),
          use_case: data.use_case || '',
          status: data.status || 'draft',
          pack_code: data.pack_code || ''  // Phase 1
        });
        
        // Fetch loops for each group from stored IDs
        await fetchPackLoops(data);
      } else {
        toast.error('Pack not found');
        navigate('/admin/sound-packs');
      }
    } catch (error) {
      toast.error('Failed to load pack');
    } finally {
      setLoading(false);
    }
  }, [pack_id, navigate]);
  
  const fetchPackLoops = async (packData) => {
    setLoopsReady(false);
    setLoadingLoops(true);
    setLoopsLoadError(false);
    try {
      const completeLoops = await loadCompletePackLoops(packData, async (loopId) => {
        const response = await fetch(`${API_URL}/api/admin/loops/${loopId}`, { credentials: 'include' });
        if (!response.ok) throw new Error('Sound request failed');
        return response.json();
      });
      // Replace membership only when every saved sound has loaded.
      setPackLoops(completeLoops);
      setLoopsReady(true);
    } catch (error) {
      setLoopsLoadError(true);
    } finally {
      setLoadingLoops(false);
    }
  };
  
  useEffect(() => {
    if (pack_id) fetchPack();
  }, [pack_id, fetchPack]);
  
  // Fetch insights when tab changes
  useEffect(() => {
    if (activeTab === 'insights' && !insights) {
      fetchInsights();
    }
  }, [activeTab]);
  
  const fetchInsights = async () => {
    setLoadingInsights(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/sound-packs/${pack_id}/insights`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setInsights(data);
      }
    } catch (error) {
      console.error('Failed to fetch insights:', error);
    } finally {
      setLoadingInsights(false);
    }
  };
  
  // Fetch available pack codes for "Load from Pack" dialog
  const fetchPackCodes = async () => {
    setLoadingPackCodes(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/pack-codes`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPackCodes(data);
      }
    } catch (error) {
      console.error('Failed to fetch pack codes:', error);
    } finally {
      setLoadingPackCodes(false);
    }
  };
  
  // Load loops from a pack code into the builder
  const loadLoopsFromPack = async () => {
    if (!selectedPackCode) {
      toast.error('Please select a pack code');
      return;
    }
    
    setLoadingFromPack(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/loops/by-pack-code/${selectedPackCode}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const loops = data.loops || [];
        
        if (loops.length === 0) {
          toast.error('No loops found for this pack code');
          return;
        }
        
        // Group loops by instrument_group
        const newPackLoops = {
          Melodies: [],
          Drums: [],
          Textures: [],
          'Sound FX': []
        };
        
        loops.forEach(loop => {
          const group = loop.instrument_group || 'Melodies';
          if (newPackLoops[group]) {
            // Add preview_url mapping for playback
            newPackLoops[group].push({
              ...loop,
              preview_url: loop.preview_url || loop.original_url
            });
          }
        });
        
        // Set the pack loops
        setPackLoops(newPackLoops);
        setShowLoadFromPack(false);
        setSelectedPackCode('');
        
        const totalLoaded = Object.values(newPackLoops).reduce((sum, g) => sum + g.length, 0);
        toast.success(`Loaded ${totalLoaded} loops from pack ${selectedPackCode}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to load loops');
      }
    } catch (error) {
      console.error('Error loading loops from pack:', error);
      toast.error('Failed to load loops from pack');
    } finally {
      setLoadingFromPack(false);
    }
  };
  
  // Open Load from Pack dialog
  const openLoadFromPack = () => {
    fetchPackCodes();
    setSelectedPackCode('');
    setShowLoadFromPack(true);
  };
  
  // Save pack metadata
  const savePack = async () => {
    if (!loopsReady || loadingLoops || saving) return;
    if (!editForm.title.trim()) {
      toast.error('Please enter a pack title');
      return;
    }
    
    if (!isPackTempo(editForm.tempo_set)) { toast.error('Enter a positive whole-number BPM.'); return; }
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/sound-packs/${pack_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editForm.title,
          description: editForm.description,
          tempo_set: editForm.tempo_set ? parseInt(editForm.tempo_set) : null,
          key: editForm.key,
          moods: editForm.moods,
          styles: editForm.styles,
          use_case: editForm.use_case,
          status: editForm.status,
          pack_code: editForm.pack_code || null,  // Phase 1: Link to loops by pack_code
          // Save loop IDs
          melody_loop_ids: packLoops.Melodies.map(l => l.loop_id),
          drum_loop_ids: packLoops.Drums.map(l => l.loop_id),
          texture_loop_ids: packLoops.Textures.map(l => l.loop_id),
          sfx_loop_ids: packLoops['Sound FX'].map(l => l.loop_id)
        })
      });
      
      if (response.ok) {
        toast.success('Pack saved successfully!');
        fetchPack(); // Refresh
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save pack');
      }
    } catch (error) {
      toast.error('Error saving pack');
    } finally {
      setSaving(false);
    }
  };
  
  // Publish pack
  const publishPack = async () => {
    if (!loopsReady || loadingLoops || saving) return;
    if (!hasMetadata) {
      toast.error('Please complete metadata first (title, tempo, key)');
      setActiveTab('metadata');
      return;
    }
    if (!hasLoops) {
      toast.error('Please add at least one loop to publish');
      setActiveTab('loops');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/sound-packs/${pack_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editForm.title,
          description: editForm.description,
          tempo_set: editForm.tempo_set ? parseInt(editForm.tempo_set) : null,
          key: editForm.key,
          moods: editForm.moods,
          styles: editForm.styles,
          use_case: editForm.use_case,
          status: 'published',
          melody_loop_ids: packLoops.Melodies.map(l => l.loop_id),
          drum_loop_ids: packLoops.Drums.map(l => l.loop_id),
          texture_loop_ids: packLoops.Textures.map(l => l.loop_id),
          sfx_loop_ids: packLoops['Sound FX'].map(l => l.loop_id)
        })
      });
      
      if (response.ok) {
        setEditForm(prev => ({ ...prev, status: 'published' }));
        toast.success('Pack published successfully! 🎉');
        fetchPack(); // Refresh to get updated data
      } else {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        toast.error(error.detail || 'Failed to publish pack');
      }
    } catch (error) {
      console.error('Publish error:', error);
      toast.error('Error publishing pack');
    } finally {
      setSaving(false);
    }
  };
  
  // Cover image upload
  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate pack_id exists
    if (!pack_id) {
      toast.error('Please save the pack first before uploading a cover image');
      return;
    }
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, or WebP images allowed');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setUploadingCover(true);
    const loadingToast = toast.loading('Uploading cover image...');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      console.log('Uploading cover to:', `${API_URL}/api/admin/sound-packs/${pack_id}/cover-image`);
      
      const response = await fetch(`${API_URL}/api/admin/sound-packs/${pack_id}/cover-image`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      console.log('Upload response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Upload success, cover_image_url:', data.cover_image_url);
        
        // Update pack state with new cover image URL
        setPack(prev => {
          console.log('Previous pack state:', prev);
          const updated = { ...prev, cover_image_url: data.cover_image_url };
          console.log('Updated pack state:', updated);
          return updated;
        });
        
        toast.dismiss(loadingToast);
        toast.success('Cover image uploaded successfully!');
      } else {
        toast.dismiss(loadingToast);
        const errorText = await response.text();
        console.error('Upload failed:', response.status, errorText);
        try {
          const error = JSON.parse(errorText);
          toast.error(error.detail || 'Failed to upload cover image');
        } catch {
          toast.error(`Upload failed (${response.status})`);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.dismiss(loadingToast);
      toast.error('Network error while uploading cover image');
    } finally {
      setUploadingCover(false);
      // Reset file input so same file can be re-selected
      if (coverInputRef.current) {
        coverInputRef.current.value = '';
      }
    }
  };
  
  // Search loops for picker - with wider BPM range
  const searchLoops = async () => {
    setSearching(true);
    setSearchError(null);
    try {
      // The picker offers the pack's own tempo, and only that.
      //
      // It used to search the pack tempo plus or minus 30, so a 90 BPM pack was
      // offered 70s, 80s, 110s and 120s with nothing marking which actually
      // matched. Staff then had to check every result by eye, and anything that
      // slipped through put an off-tempo loop in a pack students trust to be
      // one tempo.
      //
      // Exact, not a range. A range cannot express "this tempo only": asking
      // for half to double the pack tempo would have offered a 90 BPM pack
      // everything from 45 to 180, which is wider than the window it replaced.
      const packTempo = editForm.tempo_set ? parseInt(editForm.tempo_set) : null;
      const bpmMin = packTempo || 60;
      const bpmMax = packTempo || 200;
      
      const params = new URLSearchParams({
        search: searchQuery,
        bpm_min: bpmMin,
        bpm_max: bpmMax,
        limit: 100
      });
      
      // Only add instrument_group filter if toggle is enabled
      if (filterByGroup && pickerGroup) {
        params.append('instrument_group', pickerGroup);
      }
      
      if (searchMood) params.append('mood_category', searchMood);
      if (searchStyle) params.append('style_tags', searchStyle);
      if (searchKey && pickerGroup === 'Melodies') params.append('key', searchKey);
      
      const response = await fetch(`${API_URL}/api/admin/sound-packs/search-loops?${params.toString()}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
        if (data.length === 0) {
          setSearchError(`No loops found matching filters. Try clearing some filters or widening your search.`);
        }
      } else {
        const error = await response.json();
        setSearchError(error.detail || 'Search failed');
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchError('Failed to search loops. Please try again.');
    } finally {
      setSearching(false);
    }
  };
  
  // Auto-search when picker opens or filters change
  useEffect(() => {
    if (showLoopPicker) {
      searchLoops();
    }
  }, [showLoopPicker, pickerGroup, filterByGroup]);
  
  // Add selected loops to pack
  const addSelectedLoops = () => {
    const newLoops = searchResults.filter(l => selectedLoops.includes(l.loop_id));
    setPackLoops(prev => ({
      ...prev,
      [pickerGroup]: [...prev[pickerGroup], ...newLoops]
    }));
    setSelectedLoops([]);
    setShowLoopPicker(false);
    toast.success(`Added ${newLoops.length} ${pickerGroup.toLowerCase()} to pack!`);
  };
  
  // Remove loop from pack
  const removeLoop = (group, loopId) => {
    setPackLoops(prev => ({
      ...prev,
      [group]: prev[group].filter(l => l.loop_id !== loopId)
    }));
    toast.success('Loop removed');
  };
  
  // Toggle loop selection
  const toggleLoopSelection = (loopId) => {
    setSelectedLoops(prev => 
      prev.includes(loopId) 
        ? prev.filter(id => id !== loopId)
        : [...prev, loopId]
    );
  };
  
  // Export pack as ZIP
  const exportPack = async () => {
    if (totalLoops === 0) {
      toast.error('Add loops to the pack before exporting');
      return;
    }
    
    setExporting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/sound-packs/${pack_id}/export`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          allow_empty_groups: true,
          include_manifest: true
        })
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.headers.get('Content-Disposition')?.split('filename=')[1]?.replace(/"/g, '') || 'pack_export.zip';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Pack exported!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Export failed');
      }
    } catch (error) {
      toast.error('Export error');
    } finally {
      setExporting(false);
    }
  };
  
  // Export insights CSV
  const exportInsightsCsv = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/sound-packs/${pack_id}/insights/export`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pack_insights_${pack_id}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Insights exported');
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };
  
  // Play loop preview
  const handlePlayLoop = (loop) => {
    // Check if loop has preview URL
    if (!loop.preview_url) {
      toast.error('Preview not available for this loop');
      return;
    }
    
    if (currentLoop?.loop_id === loop.loop_id && isPlaying) {
      stop();
    } else {
      playLoop(loop);
    }
  };
  
  // Toggle mood
  const toggleMood = (mood) => {
    setEditForm(prev => ({
      ...prev,
      moods: prev.moods.includes(mood)
        ? prev.moods.filter(m => m !== mood)
        : [...prev.moods, mood]
    }));
  };
  
  // Toggle style
  const toggleStyle = (style) => {
    setEditForm(prev => ({
      ...prev,
      styles: prev.styles.includes(style)
        ? prev.styles.filter(s => s !== style)
        : [...prev.styles, style]
    }));
  };
  
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
      </div>
    );
  }
  
  const currentStep = getCurrentStep();
  
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Link to="/admin/sound-packs">
                <Button variant="ghost" size="icon" className="rounded-full">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading font-bold text-xl">{editForm.title || 'New Pack'}</h1>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{totalLoops} loops</span>
                  {editForm.tempo_set && <span>• {editForm.tempo_set} BPM</span>}
                  {editForm.key && <span>• {editForm.key}</span>}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={exportPack}
                disabled={exporting || totalLoops === 0}
                className="rounded-full"
                data-testid="export-pack-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                {exporting ? 'Exporting...' : 'Export ZIP'}
              </Button>
              
              <Button
                onClick={savePack}
                disabled={saving || !loopsReady || loadingLoops}
                className="rounded-full bg-primary hover:bg-primary/90 text-black"
                data-testid="save-pack-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
              
              {!isPublished ? (
                <Button
                  onClick={publishPack}
                  disabled={saving || !loopsReady || loadingLoops}
                  className="rounded-full bg-green-500 hover:bg-green-600 text-white"
                  data-testid="publish-pack-btn"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Publish
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setEditForm(prev => ({ ...prev, status: 'draft' }))}
                  className="rounded-full border-yellow-500 text-yellow-500"
                  data-testid="unpublish-pack-btn"
                >
                  <EyeOff className="w-4 h-4 mr-1" />
                  Unpublish
                </Button>
              )}
            </div>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-4 py-2 overflow-x-auto">
            <ProgressStep step={1} currentStep={currentStep} title="Metadata" isComplete={hasMetadata} />
            <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <ProgressStep step={2} currentStep={currentStep} title="Add Loops" isComplete={hasLoops} />
            <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <ProgressStep step={3} currentStep={currentStep} title="Publish" isComplete={isPublished} />
          </div>
        </div>
      </header>
      
      {(!loopsReady || loadingLoops) && (
        <div role="alert" className="border-b border-yellow-500/30 bg-yellow-500/10 px-4 py-3">
          <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-3">
            <p className="text-sm text-foreground">
              {loadingLoops ? 'Loading all pack sounds. Saving is paused.' :
                'Some pack sounds could not load. Saving and publishing are paused to protect the saved sound list.'}
            </p>
            {loopsLoadError && (
              <Button variant="outline" disabled={loadingLoops} onClick={() => pack ? fetchPackLoops(pack) : fetchPack()}>
                Retry loading sounds
              </Button>
            )}
          </div>
        </div>
      )}
      {/* Status Banner */}
      {isPublished ? (
        <div className="bg-green-500/10 border-b border-green-500/30 px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-green-400">
              <CheckCircle2 className="w-4 h-4" />
              <span>This pack is <strong>published</strong> and visible to students</span>
            </div>
            <Link to="/admin/sound-packs">
              <Button size="sm" variant="outline" className="rounded-full border-green-500/50 text-green-400 hover:bg-green-500/10">
                <ArrowLeft className="w-4 h-4 mr-1" />
                View All Packs
              </Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="bg-yellow-500/10 border-b border-yellow-500/30 px-4 py-2">
          <div className="max-w-6xl mx-auto flex items-center gap-2 text-sm text-yellow-400">
            <AlertCircle className="w-4 h-4" />
            <span>This pack is a <strong>draft</strong> - complete the steps above to publish</span>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-white/5 rounded-full p-1">
            <TabsTrigger value="metadata" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-black">
              1. Metadata {hasMetadata && <Check className="w-3 h-3 ml-1" />}
            </TabsTrigger>
            <TabsTrigger value="loops" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="loops-tab">
              2. Loops ({totalLoops}) {hasLoops && <Check className="w-3 h-3 ml-1" />}
            </TabsTrigger>
            <TabsTrigger value="insights" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="insights-tab">
              Insights
            </TabsTrigger>
          </TabsList>
          
          {/* Metadata Tab */}
          <TabsContent value="metadata" className="space-y-6" data-testid="metadata-content">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Cover Image */}
              <div className="lg:col-span-1">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <label className="block text-sm font-medium mb-3">Cover Image</label>
                  <div 
                    onClick={() => coverInputRef.current?.click()}
                    className="aspect-square rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 cursor-pointer hover:opacity-80 transition-opacity overflow-hidden relative"
                    data-testid="cover-image-upload"
                  >
                    {pack?.cover_image_url ? (
                      <img 
                        key={pack.cover_image_url}
                        src={pack.cover_image_url} 
                        alt="Cover"
                        className="w-full h-full object-cover"
                        data-testid="cover-image-preview"
                        onLoad={() => console.log('Cover image loaded successfully')}
                        onError={(e) => {
                          console.error('Cover image failed to load:', pack.cover_image_url);
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center">
                        <Image className="w-12 h-12 text-white/30 mb-2" />
                        <span className="text-sm text-muted-foreground">Click to upload</span>
                      </div>
                    )}
                    {uploadingCover && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center" data-testid="cover-upload-loading">
                        <Loader2 className="w-8 h-8 text-primary animate-spin" />
                      </div>
                    )}
                  </div>
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleCoverUpload}
                    className="hidden"
                    data-testid="cover-image-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2">Square image recommended (1:1), max 5MB</p>
                </div>
              </div>
              
              {/* Metadata Form */}
              <div className="lg:col-span-2 space-y-4">
                {/* Title & Description */}
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Title <span className="text-red-400">*</span>
                    </label>
                    <Input
                      value={editForm.title}
                      onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Pack title (e.g., Chill Classroom Vibes)"
                      className="rounded-lg"
                      data-testid="pack-title-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <Textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Short description for students (1-3 lines)"
                      className="rounded-lg resize-none"
                      rows={2}
                      data-testid="pack-description-input"
                    />
                  </div>
                </div>
                
                {/* Musical Properties */}
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <label className="block text-sm font-medium mb-3">
                    Musical Properties <span className="text-red-400">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Tempo Set</label>
                      <Input type="number" min="1" step="1" inputMode="numeric" aria-label="Pack tempo (BPM)" data-testid="pack-tempo-input"
                        value={editForm.tempo_set}
                        onChange={event => setEditForm(prev => ({ ...prev, tempo_set: event.target.value }))}
                        placeholder="Exact BPM, e.g. 135" className="rounded-lg" />
                      <p className="text-xs text-muted-foreground mt-1">Enter the exact loop tempo, including 135 or 136.</p>
                    </div>
                    
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Key</label>
                      <Select
                        value={editForm.key}
                        onValueChange={(v) => setEditForm(prev => ({ ...prev, key: v }))}
                      >
                        <SelectTrigger className="rounded-lg" data-testid="pack-key-select">
                          <SelectValue placeholder="Key" />
                        </SelectTrigger>
                        <SelectContent>
                          {MUSICAL_KEYS.map(key => (
                            <SelectItem key={key} value={key}>{key}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Use Case</label>
                      <Select
                        value={editForm.use_case}
                        onValueChange={(v) => setEditForm(prev => ({ ...prev, use_case: v }))}
                      >
                        <SelectTrigger className="rounded-lg" data-testid="pack-usecase-select">
                          <SelectValue placeholder="Use case" />
                        </SelectTrigger>
                        <SelectContent>
                          {USE_CASES.map(uc => (
                            <SelectItem key={uc} value={uc}>{uc}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                
                {/* Moods */}
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <label className="block text-sm font-medium mb-2">Moods (optional)</label>
                  <div className="flex flex-wrap gap-2">
                    {MOOD_CATEGORIES.map(mood => (
                      <Button
                        key={mood}
                        size="sm"
                        variant={editForm.moods.includes(mood) ? 'default' : 'outline'}
                        onClick={() => toggleMood(mood)}
                        className={`rounded-full text-xs ${editForm.moods.includes(mood) ? 'bg-primary text-black' : ''}`}
                      >
                        {mood}
                      </Button>
                    ))}
                  </div>
                </div>
                
                {/* Styles */}
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <label className="block text-sm font-medium mb-2">Styles (optional)</label>
                  <div className="flex flex-wrap gap-2">
                    {STYLE_TAGS.map(style => (
                      <Button
                        key={style}
                        size="sm"
                        variant={editForm.styles.includes(style) ? 'default' : 'outline'}
                        onClick={() => toggleStyle(style)}
                        className={`rounded-full text-xs ${editForm.styles.includes(style) ? 'bg-secondary text-white' : ''}`}
                      >
                        {style}
                      </Button>
                    ))}
                  </div>
                </div>
                
                {/* Pack Code - Links to loops tagged with this code */}
                <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4">
                  <label className="block text-sm font-medium mb-2 text-amber-400">
                    Pack Code (optional)
                  </label>
                  <Input
                    value={editForm.pack_code}
                    onChange={(e) => setEditForm(prev => ({ ...prev, pack_code: e.target.value.toUpperCase() }))}
                    placeholder="e.g., NH90, PG70"
                    className="rounded-lg bg-white/5 border-amber-500/30 uppercase"
                    data-testid="pack-code-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Loops tagged with this pack code will automatically appear in this pack on the frontend.
                    Use the same code when uploading loops to link them here.
                  </p>
                </div>
                
                {/* Next Step Button */}
                {hasMetadata && (
                  <div className="flex justify-end">
                    <Button
                      onClick={() => setActiveTab('loops')}
                      className="rounded-full bg-primary hover:bg-primary/90 text-black"
                    >
                      Next: Add Loops
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
          
          {/* Loops Tab */}
          <TabsContent value="loops" className="space-y-6">
            {!hasMetadata && (
              <div className="rounded-2xl border border-yellow-500/30 bg-yellow-500/10 p-4 mb-4">
                <div className="flex items-center gap-2 text-yellow-400">
                  <AlertCircle className="w-5 h-5" />
                  <span>Complete metadata first (title, tempo, key) before adding loops</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setActiveTab('metadata')}
                    className="ml-auto text-yellow-400"
                  >
                    Go to Metadata
                  </Button>
                </div>
              </div>
            )}
            
            {/* Load Loops from Pack Button */}
            {hasMetadata && (
              <div className="flex items-center justify-between p-4 rounded-2xl border border-amber-500/30 bg-amber-500/5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <Download className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-400">Load Loops from Pack</h3>
                    <p className="text-xs text-muted-foreground">
                      Instantly load loops that were assigned to a pack during upload
                    </p>
                  </div>
                </div>
                <Button
                  onClick={openLoadFromPack}
                  className="rounded-full bg-amber-500 hover:bg-amber-600 text-black"
                  data-testid="load-from-pack-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Load Loops from Pack
                </Button>
              </div>
            )}
            
            {PACK_GROUPS.map(group => (
              <div key={group} className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden" data-testid={`pack-group-${group.toLowerCase().replace(' ', '-')}`}>
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <Music className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold">{group}</h3>
                    <Badge className="bg-white/10 text-white border-0">{packLoops[group].length}</Badge>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      setPickerGroup(group);
                      setShowLoopPicker(true);
                      setSelectedLoops([]);
                      setSearchError(null);
                      setFilterByGroup(false);
                    }}
                    disabled={!hasMetadata}
                    className="rounded-full bg-primary/20 text-primary hover:bg-primary/30"
                    data-testid={`add-${group.toLowerCase().replace(' ', '-')}-btn`}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add {group}
                  </Button>
                </div>
                
                {packLoops[group].length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Library className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No {group.toLowerCase()} added yet</p>
                    <p className="text-xs mt-1">Click "Add {group}" to search and add loops</p>
                  </div>
                ) : (
                  <div className="p-4 space-y-2">
                    {packLoops[group].map((loop, idx) => (
                      <div
                        key={loop.loop_id}
                        className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        <span className="text-xs text-muted-foreground w-6">{idx + 1}</span>
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handlePlayLoop(loop)}
                          className="w-8 h-8 p-0 rounded-full"
                        >
                          {currentLoop?.loop_id === loop.loop_id && isPlaying ? (
                            <Pause className="w-4 h-4" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </Button>
                        
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{loop.title}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{loop.bpm} BPM</span>
                            {loop.key && <span>• {loop.key}</span>}
                          </div>
                        </div>
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeLoop(group, loop.loop_id)}
                          className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            {/* Next Step / Publish Button */}
            {hasLoops && !isPublished && (
              <div className="flex justify-end gap-2">
                <Button
                  onClick={savePack}
                  disabled={saving || !loopsReady || loadingLoops}
                  variant="outline"
                  className="rounded-full"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save Draft
                </Button>
                <Button
                  onClick={publishPack}
                  disabled={saving || !loopsReady || loadingLoops}
                  className="rounded-full bg-green-500 hover:bg-green-600 text-white"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Publish Pack
                </Button>
              </div>
            )}
          </TabsContent>
          
          {/* Insights Tab */}
          <TabsContent value="insights">
            {loadingInsights ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
              </div>
            ) : insights ? (
              <div className="space-y-6">
                {/* Header with Export */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">Pack Performance</h2>
                    <p className="text-sm text-muted-foreground">Analytics for {insights.title}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportInsightsCsv}
                    className="rounded-full"
                  >
                    <FileDown className="w-4 h-4 mr-1" />
                    Export CSV
                  </Button>
                </div>
                
                {/* Key Metrics - Large Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 to-primary/5 p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                      <p className="text-xs text-muted-foreground">Pack Selections</p>
                    </div>
                    <p className="text-3xl font-bold text-primary">{insights.metrics.pack_selected_count}</p>
                    <p className="text-xs text-muted-foreground mt-1">students picked this pack</p>
                  </div>
                  
                  <div className="rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-500/10 to-violet-500/5 p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                        <Eye className="w-4 h-4 text-violet-400" />
                      </div>
                      <p className="text-xs text-muted-foreground">Pack Views</p>
                    </div>
                    <p className="text-3xl font-bold text-violet-400">{insights.metrics.pack_views}</p>
                    <p className="text-xs text-muted-foreground mt-1">times viewed in library</p>
                  </div>
                  
                  <div className="rounded-2xl border border-lime-500/20 bg-gradient-to-br from-lime-500/10 to-lime-500/5 p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-lime-500/20 flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-lime-400" />
                      </div>
                      <p className="text-xs text-muted-foreground">Reflect Usage</p>
                    </div>
                    <p className="text-3xl font-bold text-lime-400">{insights.metrics.reflect_usage_count}</p>
                    <p className="text-xs text-muted-foreground mt-1">used in Reflect sessions</p>
                  </div>
                  
                  <div className="rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 text-amber-400" />
                      </div>
                      <p className="text-xs text-muted-foreground">Completion Rate</p>
                    </div>
                    <p className="text-3xl font-bold text-amber-400">{insights.metrics.reflect_completion_rate}%</p>
                    <p className="text-xs text-muted-foreground mt-1">sessions completed</p>
                  </div>
                </div>
                
                {/* Secondary Metrics */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-center">
                    <Clock className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                    <p className="text-2xl font-bold">{insights.metrics.avg_session_duration_minutes}</p>
                    <p className="text-xs text-muted-foreground">avg. minutes per session</p>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-center">
                    <Plus className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                    <p className="text-2xl font-bold">{insights.metrics.loops_added_to_player_count}</p>
                    <p className="text-xs text-muted-foreground">loops added to player</p>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-center">
                    <Download className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                    <p className="text-2xl font-bold">{insights.metrics.export_count}</p>
                    <p className="text-xs text-muted-foreground">ZIP downloads</p>
                  </div>
                </div>
                
                {/* Instrument Balance - Visual Bar Chart */}
                <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Music className="w-4 h-4 text-primary" />
                    Instrument Balance
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(insights.group_counts).map(([group, count]) => {
                      const total = Object.values(insights.group_counts).reduce((a, b) => a + b, 0);
                      const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                      // dotForGroup is the shared mapping; the local copy that
                      // used to sit here had Drums orange and Textures blue.
                      return (
                        <div key={group} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span>{group}</span>
                            <span className="text-muted-foreground">{count} loops ({percentage}%)</span>
                          </div>
                          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${dotForGroup(group)} transition-all duration-500`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Engagement Funnel */}
                <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-primary" />
                    Engagement Funnel
                  </h3>
                  <div className="flex items-end justify-between gap-2 h-32">
                    {[
                      { label: 'Views', value: insights.metrics.pack_views, color: 'bg-violet-500' },
                      { label: 'Selections', value: insights.metrics.pack_selected_count, color: 'bg-primary' },
                      { label: 'Reflect', value: insights.metrics.reflect_usage_count, color: 'bg-lime-500' },
                      { label: 'Completed', value: insights.metrics.reflect_completion_count, color: 'bg-amber-500' }
                    ].map((item, idx) => {
                      const maxVal = Math.max(insights.metrics.pack_views, insights.metrics.pack_selected_count, insights.metrics.reflect_usage_count, insights.metrics.reflect_completion_count, 1);
                      const height = (item.value / maxVal) * 100;
                      return (
                        <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                          <span className="text-xs font-medium">{item.value}</span>
                          <div className="w-full bg-white/10 rounded-t-lg relative" style={{ height: '80px' }}>
                            <div 
                              className={`absolute bottom-0 w-full ${item.color} rounded-t-lg transition-all duration-700`}
                              style={{ height: `${height}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-muted-foreground">{item.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-20">
                <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">No insights available yet</p>
                <p className="text-sm text-muted-foreground mt-1">Insights will appear after students start using this pack</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Loop Picker Dialog */}
      <Dialog open={showLoopPicker} onOpenChange={setShowLoopPicker}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Library className="w-5 h-5" />
              Add {pickerGroup} to Pack
            </DialogTitle>
          </DialogHeader>
          
          {/* Group Tabs */}
          <div className="flex gap-2 mb-4 overflow-x-auto">
            {PACK_GROUPS.map(group => (
              <Button
                key={group}
                size="sm"
                variant={pickerGroup === group ? 'default' : 'outline'}
                onClick={() => {
                  setPickerGroup(group);
                  setSelectedLoops([]);
                }}
                className={`rounded-full flex-shrink-0 ${pickerGroup === group ? 'bg-primary text-black' : ''}`}
              >
                {group}
              </Button>
            ))}
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchLoops()}
                className="rounded-full"
              />
            </div>
            
            <Select value={searchMood || "all"} onValueChange={(v) => { setSearchMood(v === "all" ? "" : v); }}>
              <SelectTrigger className="w-32 rounded-full">
                <SelectValue placeholder="Mood" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Moods</SelectItem>
                {MOOD_CATEGORIES.map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={searchStyle || "all"} onValueChange={(v) => { setSearchStyle(v === "all" ? "" : v); }}>
              <SelectTrigger className="w-32 rounded-full">
                <SelectValue placeholder="Style" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Styles</SelectItem>
                {STYLE_TAGS.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {pickerGroup === 'Melodies' && (
              <Select value={searchKey || "all"} onValueChange={(v) => { setSearchKey(v === "all" ? "" : v); }}>
                <SelectTrigger className="w-32 rounded-full">
                  <SelectValue placeholder="Key" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Keys</SelectItem>
                  {MUSICAL_KEYS.map(k => (
                    <SelectItem key={k} value={k}>{k}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            <Button
              size="sm"
              onClick={searchLoops}
              className="rounded-full bg-primary text-black"
            >
              <Search className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Search Info */}
          <div className="flex items-center justify-between mb-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-3">
              <span>
                Adding to <strong>{pickerGroup}</strong>
                {editForm.tempo_set && ` • ${editForm.tempo_set} BPM (exact)`}
              </span>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterByGroup}
                  onChange={(e) => setFilterByGroup(e.target.checked)}
                  className="w-3 h-3 rounded"
                />
                <span className="text-xs">Only show {pickerGroup}</span>
              </label>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setSearchMood('');
                setSearchStyle('');
                setSearchKey('');
                setSearchQuery('');
                setFilterByGroup(false);
                setTimeout(searchLoops, 100);
              }}
              className="text-xs h-6 px-2"
            >
              Clear All Filters
            </Button>
          </div>
          
          {/* Results */}
          <div className="flex-1 overflow-y-auto space-y-2 min-h-[300px]">
            {searching ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : searchError ? (
              <div className="text-center py-10">
                <AlertCircle className="w-10 h-10 mx-auto text-yellow-500 mb-2" />
                <p className="text-muted-foreground">{searchError}</p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSearchMood('');
                    setSearchStyle('');
                    setSearchKey('');
                    setSearchQuery('');
                    setTimeout(searchLoops, 100);
                  }}
                  className="mt-2"
                >
                  Clear Filters
                </Button>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <Library className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No loops found</p>
                <p className="text-sm">Try adjusting your search or filters</p>
              </div>
            ) : (
              searchResults.map(loop => {
                const isSelected = selectedLoops.includes(loop.loop_id);
                const isInPack = packLoops[pickerGroup].some(l => l.loop_id === loop.loop_id);
                
                return (
                  <div
                    key={loop.loop_id}
                    onClick={() => !isInPack && toggleLoopSelection(loop.loop_id)}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all cursor-pointer ${
                      isInPack 
                        ? 'bg-green-500/10 border border-green-500/30 opacity-50 cursor-not-allowed'
                        : isSelected 
                          ? 'bg-primary/20 border border-primary/50' 
                          : 'bg-white/5 hover:bg-white/10 border border-transparent'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isInPack ? 'bg-green-500/20' : isSelected ? 'bg-primary' : 'bg-white/10'
                    }`}>
                      {isInPack ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : isSelected ? (
                        <Check className="w-4 h-4 text-black" />
                      ) : null}
                    </div>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlayLoop(loop);
                      }}
                      className="w-8 h-8 p-0 rounded-full flex-shrink-0"
                    >
                      {currentLoop?.loop_id === loop.loop_id && isPlaying ? (
                        <Pause className="w-4 h-4" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </Button>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{loop.title}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                        <span>{loop.bpm} BPM</span>
                        {loop.key && <span>• {loop.key}</span>}
                        {loop.mood_category && <span>• {loop.mood_category}</span>}
                      </div>
                    </div>
                    
                    {isInPack && (
                      <Badge className="bg-green-500/20 text-green-500 border-0 text-xs flex-shrink-0">
                        In Pack
                      </Badge>
                    )}
                  </div>
                );
              })
            )}
          </div>
          
          <DialogFooter className="border-t border-white/10 pt-4">
            <div className="flex items-center justify-between w-full">
              <p className="text-sm text-muted-foreground">
                {selectedLoops.length} selected • {searchResults.length} found
              </p>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setShowLoopPicker(false)}
                  className="rounded-full"
                  data-testid="loop-picker-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={addSelectedLoops}
                  disabled={selectedLoops.length === 0}
                  className="rounded-full bg-primary text-black"
                  data-testid="loop-picker-add-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add {selectedLoops.length} Loops
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Load from Pack Dialog */}
      <Dialog open={showLoadFromPack} onOpenChange={setShowLoadFromPack}>
        <DialogContent className="max-w-md" data-testid="load-from-pack-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-amber-400" />
              Load Loops from Pack
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Select a pack code to load all loops that were assigned to it during the upload process.
            </p>
            
            {loadingPackCodes ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
              </div>
            ) : packCodes.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-muted-foreground">No pack codes found</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Assign loops to a pack during upload to use this feature
                </p>
              </div>
            ) : (
              <Select
                value={selectedPackCode}
                onValueChange={setSelectedPackCode}
              >
                <SelectTrigger className="rounded-xl" data-testid="pack-code-dropdown">
                  <SelectValue placeholder="Select a pack code..." />
                </SelectTrigger>
                <SelectContent>
                  {packCodes.map(pack => (
                    <SelectItem key={pack.pack_code} value={pack.pack_code}>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-amber-400">{pack.pack_code}</span>
                        <span className="text-muted-foreground">-</span>
                        <span>{pack.pack_name || 'Unnamed'}</span>
                        <Badge className="ml-2 bg-white/10 text-white border-0 text-xs">
                          {pack.loop_count} loops
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {selectedPackCode && (
              <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-amber-400" />
                  <span>
                    Ready to load loops from <span className="font-bold text-amber-400">{selectedPackCode}</span>
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  This will replace any currently added loops in the builder
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowLoadFromPack(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={loadLoopsFromPack}
              disabled={!selectedPackCode || loadingFromPack}
              className="rounded-full bg-amber-500 hover:bg-amber-600 text-black"
              data-testid="load-from-pack-confirm"
            >
              {loadingFromPack ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Load Loops
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SoundPackBuilder;
